import React, { useState } from "react";
import { numVal, parseNum, fmtAuditDate } from "./utils";

/**
 * HoursCell — editable hours input with auto-fill floor + audit stamp.
 *
 * Rules (parallels MarkupCell):
 *   - When a product+work-type is picked, the catalogue auto-fills `autoHrs`.
 *   - Going BELOW `autoHrs` is restricted to admins, managers, and users who
 *     have been individually granted `can_subtract_hours`. Anyone else gets
 *     snapped back with a warning.
 *   - Going ABOVE `autoHrs` is restricted to admins, managers, and users
 *     with `can_add_hours`.
 *   - Any committed value that differs from `autoHrs` records an audit stamp
 *     (overrideBy initials + overrideAt timestamp) — same pattern as markup.
 */
export function HoursCell({
  value,
  autoHrs,
  userInitials = "",
  overrideBy = "",
  overrideAt = "",
  onChangeWithAudit,
  testId,
  canOverrideFloor = false,   // admin or manager
  canAdd = true,              // per-user can_add_hours (ignored when canOverrideFloor)
  canSubtract = true,         // per-user can_subtract_hours (ignored when canOverrideFloor)
  disabled = false,
  title,
}) {
  const [warn, setWarn] = useState("");
  const hasAuto = autoHrs != null && autoHrs !== "";
  const autoNum = Number(autoHrs || 0);
  const cur = Number(value || 0);
  const lockedDirection = hasAuto && !canOverrideFloor && (!canAdd || !canSubtract);

  const commit = (raw) => {
    let v = parseNum(raw);
    if (v < 0) v = 0;
    if (hasAuto && !canOverrideFloor) {
      if (!canAdd && v > autoNum) { v = autoNum; setWarn(`Max ${autoNum}h — admin/manager to override`); window.setTimeout(() => setWarn(""), 2200); }
      if (!canSubtract && v < autoNum) { v = autoNum; setWarn(`Min ${autoNum}h — admin/manager to override`); window.setTimeout(() => setWarn(""), 2200); }
    }
    if (v === cur) return;
    // Audit stamp whenever the value differs from the auto-fill (matches the
    // markup pattern: any non-default value records who changed it & when).
    const stamping = hasAuto && Math.abs(v - autoNum) > 1e-6;
    onChangeWithAudit(v, stamping ? {
      hours_override_by: userInitials,
      hours_override_at: new Date().toISOString(),
    } : {
      hours_override_by: "",
      hours_override_at: "",
    });
  };

  return (
    <div className="relative">
      <input
        type="number"
        step="0.25"
        min="0"
        value={numVal(value)}
        onChange={(e) => commit(e.target.value)}
        disabled={disabled}
        readOnly={disabled}
        className={`qb-input h-9 font-mono ${lockedDirection ? "bg-amber-50 border-amber-200" : ""}`}
        data-testid={testId}
        title={title || (lockedDirection && hasAuto
          ? `Auto-filled = ${autoNum}h. ${!canAdd ? "You can't add hours. " : ""}${!canSubtract ? "You can't subtract hours." : ""}`.trim()
          : undefined)}
      />
      {overrideBy && hasAuto && Math.abs(cur - autoNum) > 1e-6 && (
        <div className="absolute left-0 -bottom-3.5 text-[9px] text-slate-500 font-mono whitespace-nowrap pointer-events-none" data-testid={testId ? `${testId}-audit` : undefined}>
          {overrideBy} · {fmtAuditDate(overrideAt)}
        </div>
      )}
      {warn && (
        <div className="absolute left-0 top-full mt-0.5 text-[10px] text-amber-700 font-medium whitespace-nowrap pointer-events-none">
          {warn}
        </div>
      )}
    </div>
  );
}
